import React from 'react';
import {
    IconSectionData,
    MediaLogosOrReviewsSection,
    HealthBenefitsSection,
    Statement,
    IconMedia,
} from '../../../types/KontentProps';

import ReviewStar from './ReviewStar.svg';
import LazyImage from '../Global/LazyImage';

const renderMediaLogosOrReviews = (
    section: MediaLogosOrReviewsSection,
    styles: any,
    bg: string,
    color: string
) => {
    const style = section.icon_section_style?.value?.[0]?.codename;
    if (!style) return null;

    return (
        <div className={styles.mediaContainer}>
            <hr className={styles.horizontalLine} />
            {/* Title */}
            <p
                className={styles.mediaTitle}
                data-kontent-element-codename="icon_section_title"
            >
                {section?.icon_section_title?.value}
            </p>

            {/* Stars */}
            <div className={styles.stars}>
                {Array.from({ length: 5 }).map((_, idx) => (
                    <LazyImage
                        src={ReviewStar}
                        alt="Review star"
                        className={styles.starIcon}
                        key={idx}
                        width={21}
                        height={20}
                    />
                ))}
            </div>

            {/* Logos only if media_logos */}
            {style === 'media_logos' && (
                <div className={styles.logoGrid}>
                    {section.icon_statements.value.map((item) => {
                        if (!('file' in (item.elements || {}))) return null;

                        const mediaItem = item as IconMedia;

                        return mediaItem.elements.file.value.map(
                            (asset, idx) => {
                                if (!asset?.url) return null;

                                return (
                                    <div className={styles.logoItem} key={idx}>
                                        <LazyImage
                                            src={asset.url}
                                            alt={
                                                asset.description ||
                                                'Media logo'
                                            }
                                            className={styles.logoImage}
                                            // Query selects width but not
                                            // height; fall back to the
                                            // 130x85 max box from CSS.
                                            width={
                                                asset.width && asset.height
                                                    ? asset.width
                                                    : 130
                                            }
                                            height={
                                                asset.width && asset.height
                                                    ? asset.height
                                                    : 85
                                            }
                                        />
                                    </div>
                                );
                            }
                        );
                    })}
                </div>
            )}
        </div>
    );
};

const renderHealthBenefits = (
    section: HealthBenefitsSection,
    styles: any,
    bg: string,
    color: string
) => {
    if (!section.icon_statements.value.length) return null;

    return (
        <div
            className={styles.columnContainer2}
            style={{
                backgroundColor: bg || 'inherit',
                color: color || 'inherit',
            }}
        >
            <hr className={styles.horizontalLine} />
            <div className={styles.iconGrid}>
                {section.icon_statements.value.map((item) => {
                    const statement = item as Statement;
                    const title = statement.elements.display_title?.value;
                    const iconUrl =
                        statement.elements.icon?.value?.[0]?.elements
                            ?.icon_svg_file?.value?.[0]?.url;

                    return (
                        <div className={styles.iconItem} key={title}>
                            {iconUrl && (
                                <LazyImage
                                    src={iconUrl}
                                    alt={title || 'Icon'}
                                    className={styles.iconImage}
                                    // Box fixed by CSS (38x38 / 29x27).
                                    width={38}
                                    height={38}
                                />
                            )}
                            {title && (
                                <p
                                    className={styles.iconTitle}
                                    data-kontent-element-codename="display_title"
                                >
                                    {title}
                                </p>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

const isMediaLogosOrReviewsSection = (
    section: IconSectionData
): section is MediaLogosOrReviewsSection => {
    const codename = section.icon_section_style?.value?.[0]?.codename;
    return codename === 'media_logos' || codename === 'reviews';
};

const isHealthBenefitsSection = (
    section: IconSectionData
): section is HealthBenefitsSection => {
    const codename = section.icon_section_style?.value?.[0]?.codename;
    return codename === 'health_benefits';
};

export const renderIconSection = (
    section: IconSectionData,
    healthSectionstyles: any,
    mediaLogoSectionStyles: any,
    bg: string,
    color: string
) => {
    const style = section.icon_section_style?.value?.[0]?.codename;

    if (!style || style === 'hide') {
        return null;
    }

    if (isMediaLogosOrReviewsSection(section)) {
        return renderMediaLogosOrReviews(
            section,
            mediaLogoSectionStyles,
            bg,
            color
        );
    }

    if (isHealthBenefitsSection(section)) {
        return renderHealthBenefits(section, healthSectionstyles, bg, color);
    }

    return null;
};
